import { defineMessages } from 'react-intl';

import { FlagType } from '~/flags/flags';
import { getFlag } from '~/flags/useFlag';
import { intlService } from '~/i18n/IntlService';
import {
  ENTITIES_EDIT_ID,
  ENTITIES_VIEW_ID,
  ENTITY_TYPE_MANAGE_ID,
} from '~/permissions/rbac/constants';

const messages = defineMessages({
  groupsGroupName: {
    id: 'GetPermissionsResponse.groupsGroupName',
    defaultMessage: 'Groups',
    description: 'Permission group title for Groups',
  },
  groupsEditName: {
    id: 'GetPermissionsResponse.groupsEditName',
    defaultMessage: 'Groups: Edit',
    description: 'Permission name for editing groups',
  },
  groupsEditDescription: {
    id: 'GetPermissionsResponse.groupsEditDescription',
    defaultMessage:
      'Create, view, edit, or delete groups. Can also view or edit individual files in groups subject to folder access control settings.',
    description: 'Explains capabilities for editing groups',
  },
  groupsViewName: {
    id: 'GetPermissionsResponse.groupsViewName',
    defaultMessage: 'Groups: View',
    description: 'Permission name for viewing groups',
  },
  groupsViewDescription: {
    id: 'GetPermissionsResponse.groupsViewDescription',
    defaultMessage:
      'View groups. Can also view or edit individual files in groups subject to folder access control settings.',
    description: 'Explains capabilities for viewing groups',
  },
  managedEntitiesGroupName: {
    id: 'GetPermissionsResponse.managedEntitiesGroupName',
    defaultMessage: 'Managed Entities',
    description: 'Permission group title for Managed Entities',
  },
  entityTypeManageName: {
    id: 'GetPermissionsResponse.entityTypeManageName',
    defaultMessage: 'Entity Type: Manage',
    description: 'Permission name for managing entity types',
  },
  entityTypeManageDescription: {
    id: 'GetPermissionsResponse.entityTypeManageDescription',
    defaultMessage:
      'Create, view, edit, or delete entity types and their fields, including alias mappings and data-value configuration. Also grants full group and node access across every entity type, not only the one being administered, and the ability to view and edit entity records on the Documents tab.',
    description: 'Explains capabilities for managing entity types',
  },
  entitiesEditName: {
    id: 'GetPermissionsResponse.entitiesEditName',
    defaultMessage: 'Entities: Edit',
    description: 'Permission name for editing entities',
  },
  entitiesEditDescription: {
    id: 'GetPermissionsResponse.entitiesEditDescription',
    defaultMessage:
      'Create entities and edit their data values and aliases on the Documents tab.',
    description: 'Explains capabilities for editing entities',
  },
  entitiesViewName: {
    id: 'GetPermissionsResponse.entitiesViewName',
    defaultMessage: 'Entities: View',
    description: 'Permission name for viewing entities',
  },
  entitiesViewDescription: {
    id: 'GetPermissionsResponse.entitiesViewDescription',
    defaultMessage:
      'View entities, their data values and their aliases on the Documents tab.',
    description: 'Explains capabilities for viewing entities',
  },
});

const groupedTicketsAndDocsFeature = {
  editPermissionId: '8f7a4b2e-9c1d-4e3f-b5a6-7d8c9e0f1a2b',
  viewPermissionId: '3e5d7c9b-1a2f-4e6d-8c0a-5b7e9d1c3f4a',
  id: 'entity_group',
};

const getGroupsGroup = () => {
  const intl = intlService.getIntl();
  const feature = groupedTicketsAndDocsFeature;

  return {
    type: 'permission_group',
    id: 'c3837d2f-6f8e-4476-82a3-00f82d1f022c',
    attributes: {
      name: intl.formatMessage(messages.groupsGroupName),
      permissions: [
        {
          id: feature.editPermissionId,
          name: intl.formatMessage(messages.groupsEditName),
          description: intl.formatMessage(messages.groupsEditDescription),
          feature: feature.id,
          premium: true,
          relations: ['feature_creator', 'feature_editor', 'feature_deleter'],
        },
        {
          id: feature.viewPermissionId,
          name: intl.formatMessage(messages.groupsViewName),
          description: intl.formatMessage(messages.groupsViewDescription),
          premium: true,
          feature: feature.id,
          relations: ['feature_viewer'],
        },
      ],
    },
  };
};

// UUIDs mirror Pilot's role catalog fixture exactly (EKP-59161) — role save
// fails if these drift from Pilot's copy.
const getManagedEntitiesGroup = () => {
  const intl = intlService.getIntl();

  return {
    type: 'permission_group',
    id: '8ae0a165-9687-4dab-ba0f-19b6b5440bc4',
    attributes: {
      name: intl.formatMessage(messages.managedEntitiesGroupName),
      permissions: [
        {
          id: ENTITY_TYPE_MANAGE_ID,
          name: intl.formatMessage(messages.entityTypeManageName),
          description: intl.formatMessage(messages.entityTypeManageDescription),
          feature: 'entity_types',
          premium: true,
          relations: ['feature_creator', 'feature_editor', 'feature_deleter'],
        },
        {
          id: ENTITIES_EDIT_ID,
          name: intl.formatMessage(messages.entitiesEditName),
          description: intl.formatMessage(messages.entitiesEditDescription),
          feature: 'entities',
          premium: true,
          relations: ['feature_creator', 'feature_editor', 'feature_deleter'],
        },
        {
          id: ENTITIES_VIEW_ID,
          name: intl.formatMessage(messages.entitiesViewName),
          description: intl.formatMessage(messages.entitiesViewDescription),
          feature: 'entities',
          premium: true,
          relations: ['feature_viewer'],
        },
      ],
    },
  };
};

export const getGroupPermission = () => {
  const hasMesPartiesMvp = getFlag(FlagType.MesPartiesMvp);

  return [hasMesPartiesMvp ? getManagedEntitiesGroup() : getGroupsGroup()];
};
